/**
 * Campaigns — marketing programmes with a budget and a run window.
 *
 * A budget is in the campaign's own currency, which starts as the workspace
 * currency (CUR-2).
 */
import { useCurrencyCreateField } from "@/components/CurrencyField";
import { ResourceListPage } from "@/components/ResourceListPage";
import { StatusBadge } from "@/components/ui";
import { formatDate, formatMoney } from "@/lib/money";
import type { CampaignRow } from "./types";

export function CampaignsPage() {
  const currencyField = useCurrencyCreateField();
  return (
    <ResourceListPage<CampaignRow>
      title="Campaigns"
      subtitle="Marketing programmes with a budget and a run window. Leads carry the campaign they came from."
      breadcrumb={["CRM", "Campaigns"]}
      path="/crm/campaigns"
      queryKey={["crm", "campaigns"]}
      createLabel="New campaign"
      emptyTitle="No campaigns"
      emptyMessage="A campaign groups the leads a programme produced, so spend can be compared against what it returned."
      createFields={[
        {
          field: "name",
          label: "Campaign name",
          required: true,
          placeholder: "Q3 trade show",
        },
        {
          field: "status",
          label: "Status",
          options: ["Draft", "Active", "Completed", "Cancelled"],
        },
        { field: "startDate", label: "Starts", type: "date" },
        { field: "endDate", label: "Ends", type: "date" },
        { field: "budget", label: "Budget", type: "number" },
        currencyField,
      ]}
      columns={[
        { key: "name", label: "Campaign" },
        {
          key: "status",
          label: "Status",
          render: (r) => <StatusBadge status={r.status} />,
        },
        {
          key: "start_date",
          label: "Starts",
          render: (r) => (r.start_date ? formatDate(r.start_date) : "—"),
        },
        {
          key: "end_date",
          label: "Ends",
          render: (r) => (r.end_date ? formatDate(r.end_date) : "—"),
        },
        {
          key: "budget",
          label: "Budget",
          numeric: true,
          render: (r) =>
            r.budget ? formatMoney(r.budget, r.currency_code) : "—",
        },
      ]}
    />
  );
}
